import React, { useState, useEffect, useRef } from "react";
import {
  CheckIcon,
  CameraIcon,
  UploadCloudIcon,
  InsuranceIllustrationIcon,
  IdCardIcon,
  HomeIcon,
  ResidenceIcon,
} from "../../../common/Icons";
import { applicationsApi } from "../../../services/api";
import {
  ApplicationStatusDto,
  InsuranceUploadContentProps,
} from "../../../common/types";
import {
  customerInfoIdValue,
  UploadDocumentType,
} from "../../../common/helperConstants";
import { getStorage } from "../../../common/storageHelper";
import { checkMIME } from "../../../common/helperMethods";
import { toast } from "react-toastify";
import {
  AcceptsOneFileText,
  CustomerInfoIdNotFoundText,
  DataSubmittedSuccessfullyText,
  FailedPrevUpdDocText,
  FileExtensionDoesntMatchText,
  FileSizeTooLargeText,
  FileSizeTooSmallText,
  SomethingWentWrongText,
  UploadInvalidFileTypeText,
} from "../../../common/messageContainer";
import CircleSpinner from "../../../common/loader/circleSpinner";
import StyledQR from "../../../common/StyledQR";
import { FileRejection, useDropzone } from "react-dropzone";
import CanopyInsuranceConnect from "../../../common/CanopyInsuranceConnect";
import { canopyPublicAlias } from "../../../common/apiURLs";
import { DLScanModal } from "../../../common/DLScanModal";

const InsuranceUploadContent: React.FC<InsuranceUploadContentProps> = ({
  onNextStep,
  mainContentClassName,
  markStepAsCompleted,
  currentStepId,
}) => {
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const [isCanopyInsuranceConnected, setIsCanopyInsuranceConnected] =
    useState(false);
  const vehicleVinRef = useRef<any>(null);
  const infoId = getStorage(customerInfoIdValue);
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  useEffect(() => {
    fetchApplicationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepId]);

  const fetchApplicationData = async () => {
    try {
      if (!infoId) {
        return;
      }
      setIsLoader(true);
      // applicationsApi.getApplication already returns response.data
      const appData: ApplicationStatusDto =
        await applicationsApi.getApplication(infoId);

      // Handle both direct fields and nested steps structure
      let isCompleted = false;
      let filePath = null;

      const steps = appData.steps;

      if (currentStepId === 1) {
        const step = steps.driversLicense;
        if (step) {
          isCompleted = step.completed || false;
          filePath = step.filePath || null;
        }
      } else if (currentStepId === 2) {
        const step = steps.insurance;
        if (step) {
          isCompleted = step.completed || false;
          filePath = step.filePath || null;
          if (step.count && step.count === 1) {
            setIsCanopyInsuranceConnected(true);
          }
        }
      } else if (currentStepId === 3 || currentStepId === 4) {
        // Both income (step 3) and bank statement (step 4) use the same "income" step in the API
        const bankStep = steps.bank;
        if (bankStep) {
          isCompleted = bankStep.completed || false;
          filePath = bankStep.filePath || null;
        }

        const payrollStep = steps.payroll;
        if (payrollStep) {
          isCompleted = payrollStep.completed || false;
          filePath = payrollStep.filePath || null;
        }
      } else if (currentStepId === 6) {
        const step = steps.residence;
        if (step) {
          isCompleted = step.completed || false;
          filePath = step.filePath || null;
        }
      }

      setIsUploaded(isCompleted);
      setUploadedFileName(filePath);
      vehicleVinRef.current = appData.vehicleVin || null;
    } catch (error: any) {
      toast.error(error.response?.data?.error || FailedPrevUpdDocText);
    } finally {
      setIsLoader(false);
    }
  };

  const handleInteraction = () => {
    markStepAsCompleted(currentStepId);
  };

  const handleFileUpload = async (file: any) => {
    if (file) {
      try {
        let docname = "";
        if (currentStepId === 1) docname = UploadDocumentType.DRIVERS_LICENSE;
        else if (currentStepId === 2) docname = UploadDocumentType.INSURANCE;
        else if (currentStepId === 3) docname = UploadDocumentType.INCOME;
        else if (currentStepId === 4)
          docname = UploadDocumentType.BANK_STATEMENT;
        else if (currentStepId === 6) docname = UploadDocumentType.RESIDENCE;

        const response = await applicationsApi.uploadDocument(
          infoId || "",
          docname,
          file,
        );

        if (response && response?.success) {
          handleInteraction();
          setIsCameraOpen(false);
          await fetchApplicationData();
          toast.success(DataSubmittedSuccessfullyText);
        } else {
          toast.error(SomethingWentWrongText);
        }
      } catch (error: any) {
        if (error.response) {
          const errorMessage =
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.response?.data ||
            `Upload failed: ${error.response.status}`;
          toast.error(errorMessage);
        } else if (error.request) {
          toast.error(
            "No response from server. Check your network connection.",
          );
        } else {
          toast.error(error.message || "Upload failed");
        }
      } finally {
        setIsLoader(false);
      }
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsLoader(true);
    const file = event.target.files?.[0];
    await handleFileUpload(file);
  };

  const handleTakePhotoClick = () => {
    if (currentStepId === 1) {
      setIsCameraOpen(true);
    } else {
      document.getElementById("camera-input")?.click();
    }
  };

  const onCanopySuccess = async (data: any) => {
    if (!infoId) {
      toast.error(CustomerInfoIdNotFoundText);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
      return;
    }

    try {
      setIsLoader(true);
      const model = {
        pullId: data?.pull?.pull_id,
        vin: vehicleVinRef.current || "",
      };
      const response = await applicationsApi.exchangeCanopyInsurance(
        infoId,
        model,
      );

      if (response?.success) {
        handleInteraction();
        await fetchApplicationData();
        toast.success("Insurance connected successfully!");
      } else {
        toast.error(response?.message || "Failed to connect account");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
          "Failed to connect account. Please try again.",
      );
    } finally {
      setIsLoader(false);
    }
  };

  let pageTitle: string;
  let IllustrationComponent: React.FC<React.SVGProps<SVGSVGElement>>;
  let checklistItems: Array<{ text: string | React.ReactElement }> = [];
  let insuranceConnect: React.ReactNode = null;

  if (currentStepId === 1) {
    pageTitle = "Please upload a copy of your ID / Driver's License";
    IllustrationComponent = IdCardIcon;
    checklistItems = [
      { text: "Ensure the ID is valid and not expired." },
      { text: "Make sure all text is clear and legible." },
      { text: "All four corners of the document should be visible." },
      {
        text: "If uploading a Driver's License, include both front and back if information is on both sides.",
      },
    ];
  } else if (currentStepId === 6) {
    pageTitle = "Please Provide Proof of Residence";
    IllustrationComponent = ResidenceIcon;
    checklistItems = [
      { text: "Credit Card Bill" },
      { text: "Phone Bill" },
      { text: "Utility Bill" },
      {
        text: "Any Open Auto Loan bills",
      },
    ];
  } else {
    pageTitle = "Please upload a copy of your Insurance";
    IllustrationComponent = InsuranceIllustrationIcon;
    checklistItems = [
      { text: "Comprehensive and Collision are required" },
      { text: "Max deductible for Comp/Collision coverage is $2,000/$2,000" },
      {
        text: (
          <div>
            Loss Payee:
            <div className="ml-1 font-medium">Veros Credit, LLC</div>
            <div className="ml-1">P.O. Box 924153</div>
            <div className="ml-1">Fort Worth, TX 76124</div>
          </div>
        ),
      },
    ];
    insuranceConnect = (
      <CanopyInsuranceConnect
        publicAlias={canopyPublicAlias}
        userId={infoId || ""}
        isLoader={isLoader}
        onSuccess={onCanopySuccess}
        onError={(error) => {
          console.error("Insurance error:", error);
        }}
        isConnected={isCanopyInsuranceConnected}
      />
    );
  }

  const renderChecklistItems = () => (
    <>
      {currentStepId === 6 && (
        <div className="text-lg lg:text-2xl font-medium text-gray-800 mb-3">
          Any of the following documents are acceptable if they include your
          residence address
        </div>
      )}

      <div className="space-y-3 lg:space-y-4 text-gray-700 text-base lg:text-2xl">
        {checklistItems.map((item, index) => (
          <div key={`checklist-item-${index}`} className="flex items-start">
            <CheckIcon className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600 mr-2 mt-0.5 lg:mt-1 flex-shrink-0" />
            <span>{item.text}</span>
          </div>
        ))}
      </div>
    </>
  );

  const getFileName = (filePath: string | null): string => {
    if (!filePath) return "";

    // Handle both Windows (\) and Unix (/) path separators
    const normalizedPath = filePath.replace(/\\/g, "/");
    const fileName = normalizedPath.split("/").pop() || "";

    return fileName;
  };

  const getFullURL = () => {
    const qrUri = window.location.href;
    return qrUri;
  };

  const getDropFile = (
    acceptedFiles: any[],
    fileRejections: FileRejection[],
    event: any,
  ) => {
    setIsLoader(true);
    if (!infoId) {
      toast.error(CustomerInfoIdNotFoundText);
      setIsLoader(false);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
      return;
    }
    if (fileRejections.length === 0) {
      acceptedFiles.forEach(async (selectedFile: any) => {
        const mimeType = await checkMIME(selectedFile);

        if (mimeType === selectedFile.type) {
          handleFileUpload(selectedFile);
        } else {
          toast.error(FileExtensionDoesntMatchText);
          setIsLoader(false);
        }
      });
    } else {
      let errorMsg = "";
      let invMsg = "";
      let fileLarMsg = "";
      let fileSmaMsg = "";
      let multiMsg = "";
      fileRejections.forEach((fRej) => {
        fRej.errors.forEach((d: { code: string }) => {
          if (d.code === "file-invalid-type") {
            invMsg = UploadInvalidFileTypeText;
          } else if (d.code === "file-too-large") {
            fileLarMsg = FileSizeTooLargeText;
          } else if (d.code === "file-too-small") {
            fileSmaMsg = FileSizeTooSmallText;
          } else if (d.code === "too-many-files") {
            multiMsg = AcceptsOneFileText;
          }
        });
      });
      if (invMsg !== "") errorMsg += invMsg + " ";
      if (fileLarMsg !== "") errorMsg += fileLarMsg + " ";
      if (fileSmaMsg !== "") errorMsg += fileSmaMsg + " ";
      if (multiMsg !== "") errorMsg += multiMsg;

      toast.error(errorMsg);
      setIsLoader(false);
    }
  };

  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    maxFiles: 1,
    maxSize: 20971520, //20MB=20971520 bytes
    minSize: 1024, //1kb=1024 bytes
    accept: {
      "image/png": [".png"],
      "image/jpg": [".jpg"],
      "image/jpeg": [".jpeg"],
      "image/bmp": [".bmp"],
      "image/gif": [".gif"],
      "image/heic": [".heic", ".heif"],
      "application/pdf": [".pdf"],
    },
    onDrop: getDropFile,
  });

  return (
    <main className={mainContentClassName || "w-full"}>
      <div className={`${isLoader === true ? "loaderGrey" : "loaderDisplay"}`}>
        <CircleSpinner />
      </div>
      <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-lg h-full">
        <h2 className="text-xl lg:text-3xl font-semibold text-[#09517d] mb-2 lg:mb-6">
          {pageTitle}
        </h2>

        {/* Upload Status Messages */}
        {isUploaded && uploadedFileName && (
          <div className="wdbrk mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 flex items-center justify-between">
            <span>
              <strong>Previously uploaded:</strong>{" "}
              {getFileName(uploadedFileName)}
            </span>
          </div>
        )}

        <div className="flex flex-col xl:grid xl:grid-cols-5 xl:gap-12">
          <div className="mb-6 xl:mb-0 xl:col-span-3">
            <div className="mt-2 lg:mt-6">
              <div className="p-2 rounded-md flex items-center justify-center mb-2 lg:mb-6">
                <IllustrationComponent
                  className={`max-w-full rounded mx-auto h-[100px] w-auto ${
                    currentStepId === 1 ? "text-[#36a3f7]" : ""
                  }`}
                />
              </div>
              <div className="hidden md:block">{renderChecklistItems()}</div>
              {/* {insuranceConnect} */}
            </div>
          </div>

          <div className="xl:col-span-2 space-y-4 xl:space-y-6">
            {/* Show previously uploaded file in the right column on desktop - ONLY for step 3 (income) */}
            {isUploaded && uploadedFileName && currentStepId === 3 && (
              <div className="wdbrk hidden md:block p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700">
                <span className="text-sm lg:text-base">
                  <strong>Previously uploaded:</strong>
                  <br />
                  {getFileName(uploadedFileName)}
                </span>
              </div>
            )}

            {/* {currentStepId === 2 && (
              <div>
                <div className="font-semibold text-gray-700">
                  Prefer to upload instead?
                </div>
                <div className="text-sm text-gray-600">
                  You can submit your insurance document below.
                </div>
              </div>
            )} */}

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 lg:p-6 space-y-4 lg:space-y-6">
              <div
                {...getRootProps()}
                className={`block border rounded-lg p-4 lg:p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors ${
                  isDragAccept
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300"
                }`}
              >
                <input {...getInputProps()} />
                <UploadCloudIcon className="w-8 h-8 lg:w-10 lg:h-10 text-gray-400 mx-auto mb-2" />
                <p className="hidden md:block text-base lg:text-2xl text-gray-600">
                  <span className="font-semibold text-[#09517d]">
                    Click or drag
                  </span>{" "}
                  file to upload
                </p>
                <p className="block md:hidden text-base lg:text-2xl text-gray-600">
                  <span className="font-semibold text-[#09517d]">
                    Tap to upload
                  </span>
                </p>
              </div>

              <button
                type="button"
                className="w-full flex items-center justify-center p-3 lg:p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors md:hidden disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={`Take photo to upload ${
                  currentStepId === 1
                    ? "ID or Driver's License"
                    : "insurance document"
                }`}
                onClick={handleTakePhotoClick}
                disabled={isLoader}
              >
                <CameraIcon className="w-5 h-5 lg:w-6 lg:h-6 text-gray-500 mr-2" />
                <span className="hidden md:block text-base lg:text-2xl font-medium text-gray-700">
                  Click to take photo
                </span>
                <span className="block md:hidden text-base lg:text-2xl font-medium text-gray-700">
                  Tap to take photo
                </span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  id="camera-input"
                  className="sr-only"
                  onChange={handleFileChange}
                  disabled={isLoader}
                />
              </button>

              {currentStepId === 1 && isCameraOpen && (
                <DLScanModal
                  open={isCameraOpen}
                  onClose={() => setIsCameraOpen(false)}
                  isDLMode={currentStepId === 1} // ← only DL gets front+back + barcode flow
                  onCapture={async (file) => {
                    await handleFileUpload(file);
                  }}
                />
              )}

              {/* Show previously uploaded file on mobile - ONLY for step 3 (income) */}
              {isUploaded && uploadedFileName && currentStepId === 3 && (
                <div className="wdbrk md:hidden p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700">
                  <span className="text-sm">
                    <strong>Previously uploaded:</strong>
                    <br />
                    {getFileName(uploadedFileName)}
                  </span>
                </div>
              )}

              <div className="hidden md:block pt-2 lg:pt-4">
                <div
                  style={{ border: "8px solid #09517d", borderRadius: "10px" }}
                >
                  <StyledQR url={`${getFullURL()}`} />
                  <div
                    style={{
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      padding: "10px",
                      backgroundColor: "#09517d",
                      color: "white",
                      fontSize: "18px",
                      gap: "10px",
                    }}
                  >
                    <span>SCAN WITH CELL PHONE TO</span>
                    <span>TAKE PHOTO OF DOCUMENT</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="md:hidden mt-6">{renderChecklistItems()}</div>

        <div className="mt-8 lg:mt-10 text-right hidden md:block">
          <button
            onClick={onNextStep}
            type="button"
            className="bg-[#09517d] hover:bg-[#084a6f] text-white font-bold py-2.5 px-6 lg:py-3 lg:px-8 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#09517d] focus:ring-opacity-50 text-base lg:text-[1.2rem] lg:leading-[2rem] disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Proceed to next step"
            disabled={isLoader}
          >
            Next
          </button>
        </div>
      </div>
    </main>
  );
};

export default InsuranceUploadContent;
